import { ApiError } from "@/clients/core/ApiError";
import { hydrateUserSession } from "@/utils/auth/hydrate-user-session";
import { guardGetUserFromLocalStorage } from "@/utils/request";
import { useUser } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

export type HydrationStatus = "ready" | "hydrating" | "retry" | "signout";

const MAX_ATTEMPTS = 3;
const BASE_BACKOFF_MS = 300;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// "Valid" = USER_KEY present and parseable. Covers both the missing and the
// corrupted-JSON cases (guardGetUserFromLocalStorage throws for both).
const hasValidLocalUser = (): boolean => {
  try {
    guardGetUserFromLocalStorage();
    return true;
  } catch {
    return false;
  }
};

const is4xx = (error: unknown): boolean =>
  error instanceof ApiError && error.status >= 400 && error.status < 500;

/**
 * Self-heals the "signed in to Clerk but no local USER_KEY" state. When enabled
 * and USER_KEY is missing, re-hydrates it from the Clerk session + backend
 * before the protected app (and its data queries) is allowed to render.
 *
 * Returns a status the auth guard gates on:
 *  - "ready"     → USER_KEY present; safe to render children.
 *  - "hydrating" → re-fetching USER_KEY; show a spinner.
 *  - "retry"     → transient failures exhausted; offer a manual retry.
 *  - "signout"   → backend has no such user (4xx) or Clerk lacks id/email;
 *                  the guard should sign out to a clean login.
 */
export const useEnsureUserHydrated = (enabled: boolean) => {
  const { user, isLoaded: isUserLoaded } = useUser();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<HydrationStatus>(() =>
    hasValidLocalUser() ? "ready" : "hydrating",
  );
  const runningRef = useRef(false);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    if (hasValidLocalUser()) {
      setStatus("ready");
      return;
    }
    // Need the Clerk user for id + email to rebuild USER_KEY.
    if (!isUserLoaded) return;
    if (runningRef.current) return;

    const userId = user?.id;
    const email =
      user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress;
    if (!userId || !email) {
      setStatus("signout");
      return;
    }

    runningRef.current = true;
    let cancelled = false;
    setStatus("hydrating");

    (async () => {
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        try {
          await hydrateUserSession({ userId, email, queryClient });
          if (!cancelled) setStatus("ready");
          return;
        } catch (error) {
          if (is4xx(error)) {
            if (!cancelled) setStatus("signout");
            return;
          }
          if (attempt < MAX_ATTEMPTS - 1) {
            await delay(BASE_BACKOFF_MS * 2 ** attempt);
          }
        }
      }
      if (!cancelled) setStatus("retry");
    })().finally(() => {
      runningRef.current = false;
    });

    return () => {
      cancelled = true;
    };
    // `user?.id` is the stable identity; email is read from the same user object
    // within the effect, so it doesn't need its own dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isUserLoaded, user?.id, queryClient, retryNonce]);

  const retry = useCallback(() => {
    if (runningRef.current) return;
    setStatus("hydrating");
    setRetryNonce((nonce) => nonce + 1);
  }, []);

  return { status, retry };
};
